/**
 * Heads-up display.
 *
 * Kept deliberately sparse. There is no crosshair, no minimap, and no health
 * bar in the usual sense — anything that makes the screen feel managed works
 * against the fear. Meters only appear when they are close to mattering.
 */

import { TOWERS, EXTRACTION, ITEMS, clamp01 } from '@static-pines/shared';
import { MapPanel, CompassStrip, type MapModel } from './MapPanel.js';

/** Hotbar size. Matches the server's inventory cap. */
const INVENTORY_SLOTS = 6;

export interface HudModel {
  stamina: number; // 0..100
  /** True while sprint is locked out after emptying the bar. */
  exhausted: boolean;
  battery: number; // seconds
  breath: number; // 0..100
  hiding: boolean;
  health: number; // 0..100
  heldItem: string;
  /** Items occupying hotbar slots, in order. Passives are excluded. */
  hotbar: string[];
  /** Which hotbar slot is selected. */
  selectedIndex: number;
  /** Carried passives, shown as text rather than taking a slot. */
  passives: string[];
  towersActivated: number;
  towersRequired: number;
  prompt: string | null;
  /** Rescue countdown in seconds, or null when not inbound. */
  rescueTimer: number | null;
  extractionHold: number | null;
  spectating: boolean;
  spectatingName: string | null;
  /**
   * Controls hint, already worded for this device, or null when there is
   * nobody else to switch to. Built by Game, which knows the platform.
   */
  spectateHint: string | null;

  /** Set when the map is in hand. */
  map: MapModel | null;
  /** True when the compass is in hand. */
  compass: boolean;
  /** 0..1 when the radio is switched on, null otherwise. */
  radioSignal: number | null;
  /** Whether the radio scanner is switched on. */
  radioOn: boolean;
  /** Whether the torch is currently lit. */
  flashlightOn: boolean;

  /** Proximity voice readout; null when voice is not running at all. */
  voice: {
    status: 'starting' | 'live' | 'denied';
    muted: boolean;
    /** True while the local mic registers deliberate speech. */
    speaking: boolean;
    /** Noise the server attributes to this voice right now, 0..~1. */
    ownNoise: number;
    /** Names of teammates currently talking. */
    speakers: string[];
  } | null;
}

const ITEM_LABELS: Record<string, string> = {
  battery: 'Battery',
  flare: 'Flare',
  map: 'Map',
  compass: 'Compass',
  radio: 'Radio',
  booster: 'Booster',
};

/** What left-click does with each item, shown under the selected slot. */
const ITEM_HINTS: Record<string, string> = {
  battery: 'LMB  RECHARGE TORCH',
  flare: 'LMB  THROW',
};

export class Hud {
  private readonly root: HTMLDivElement;
  private readonly staminaBar: HTMLDivElement;
  private readonly staminaWrap: HTMLDivElement;
  private readonly breathBar: HTMLDivElement;
  private readonly breathWrap: HTMLDivElement;
  private readonly batteryEl: HTMLDivElement;
  private readonly batteryBar!: HTMLDivElement;
  private readonly batteryTime!: HTMLDivElement;
  private readonly batteryLabel!: HTMLDivElement;
  /** True while the torch is lit, so the meter can pulse. */
  private torchOn = false;
  private readonly itemEl: HTMLDivElement;
  private readonly inventoryEl: HTMLDivElement;
  private readonly passivesEl!: HTMLDivElement;
  private readonly objectiveEl: HTMLDivElement;
  private readonly promptEl: HTMLDivElement;
  private readonly noticeEl: HTMLDivElement;
  private readonly clueEl: HTMLDivElement;
  private readonly spectateEl: HTMLDivElement;
  private readonly damageEl: HTMLDivElement;

  private readonly mapPanel = new MapPanel();
  private readonly compassStrip = new CompassStrip();
  private readonly radioWrap: HTMLDivElement;
  private readonly radioBar: HTMLDivElement;
  private readonly radioLabel: HTMLDivElement;
  private readonly voiceEl!: HTMLDivElement;
  private readonly speakersEl!: HTMLDivElement;

  private noticeTimer = 0;
  private clueTimer = 0;
  /** Signature of the last-rendered inventory, so slots only rebuild on change. */
  private slotSignature = '';

  /**
   * Touch hooks: a tap selects and uses the slot's item, a hold drops it.
   * Wired by the game only on coarse-pointer devices; the hotbar stays inert
   * to the mouse on desktop, where the cursor is pointer-locked anyway.
   */
  onSlotTap: ((index: number) => void) | null = null;
  onSlotUse: ((index: number) => void) | null = null;
  onSlotHold: ((index: number) => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.id = 'hud';
    parent.appendChild(this.root);

    this.damageEl = div('damage-vignette');
    this.root.appendChild(this.damageEl);

    // --- bottom left: meters -------------------------------------------------
    const meters = div('hud-meters');

    this.staminaWrap = div('meter');
    this.staminaBar = div('meter-fill stamina');
    this.staminaWrap.appendChild(this.staminaBar);
    meters.appendChild(this.staminaWrap);

    this.breathWrap = div('meter');
    this.breathBar = div('meter-fill breath');
    this.breathWrap.appendChild(this.breathBar);
    meters.appendChild(this.breathWrap);

    this.root.appendChild(meters);

    // --- bottom centre: the hotbar -------------------------------------------
    const gear = div('hud-gear');
    this.inventoryEl = div('hotbar');
    this.itemEl = div('slot-caption');
    gear.append(this.inventoryEl, this.itemEl);
    this.root.appendChild(gear);

    // Passives are listed as text — they have no slot because there is
    // nothing to select or use.
    this.passivesEl = div('passives');
    this.passivesEl.style.display = 'none';
    this.root.appendChild(this.passivesEl);

    // --- bottom left: torch battery ------------------------------------------
    this.batteryEl = div('battery');
    this.root.appendChild(this.batteryEl);
    this.batteryLabel = div('battery-label');
    this.batteryLabel.textContent = 'F  TORCH';
    const batteryTrack = div('battery-track');
    this.batteryBar = div('battery-fill');
    batteryTrack.appendChild(this.batteryBar);
    this.batteryTime = div('battery-time');
    this.batteryEl.append(this.batteryLabel, batteryTrack, this.batteryTime);

    // --- top right: objective -------------------------------------------------
    this.objectiveEl = div('objective');
    this.root.appendChild(this.objectiveEl);

    // --- centre: interaction prompt and notices -------------------------------
    this.promptEl = div('prompt');
    this.root.appendChild(this.promptEl);

    this.noticeEl = div('notice');
    this.root.appendChild(this.noticeEl);

    this.clueEl = div('clue');
    this.root.appendChild(this.clueEl);

    this.spectateEl = div('spectate');
    this.root.appendChild(this.spectateEl);

    // --- Carried gear readouts ------------------------------------------------
    this.mapPanel.canvas.classList.add('gear-panel');
    this.root.appendChild(this.mapPanel.canvas);

    this.compassStrip.canvas.classList.add('gear-panel');
    this.root.appendChild(this.compassStrip.canvas);

    this.radioWrap = div('radio');
    this.radioLabel = div('radio-label');
    this.radioLabel.textContent = 'SIGNAL';
    const radioTrack = div('radio-track');
    this.radioBar = div('radio-fill');
    radioTrack.appendChild(this.radioBar);
    this.radioWrap.append(this.radioLabel, radioTrack);
    this.root.appendChild(this.radioWrap);

    // --- Voice: mic status under the torch, speakers above the meters ---------
    this.voiceEl = div('voice');
    this.root.appendChild(this.voiceEl);

    this.speakersEl = div('voice-speakers');
    this.root.appendChild(this.speakersEl);
  }

  showNotice(text: string, duration: number): void {
    this.noticeEl.textContent = text;
    this.noticeEl.classList.add('visible');
    this.noticeTimer = duration;
  }

  showClue(text: string): void {
    this.clueEl.textContent = text;
    this.clueEl.classList.add('visible');
    this.clueTimer = 14;
  }

  dismissClue(): void {
    this.clueTimer = 0;
    this.clueEl.classList.remove('visible');
  }

  get clueVisible(): boolean {
    return this.clueTimer > 0;
  }

  /** Red flash when hurt or grabbed. */
  flashDamage(): void {
    this.damageEl.classList.remove('flash');
    // Force reflow so the animation can be retriggered immediately.
    void this.damageEl.offsetWidth;
    this.damageEl.classList.add('flash');
  }

  setVisible(visible: boolean): void {
    this.root.style.display = visible ? '' : 'none';
  }

  /**
   * Numbered inventory slots.
   *
   * The number keys were previously the only way to know a slot existed, which
   * made the whole item system invisible. Slot one is always the flashlight.
   */
  /**
   * The hotbar: six fixed slots along the bottom, holding inventory only.
   *
   * Empty slots are still drawn so the bar has a stable shape and the number
   * keys always mean the same thing.
   */
  private renderSlots(model: HudModel): void {
    // radioOn is part of the key because the carried-items line shows it.
    const signature =
      `${model.hotbar.join('|')}::${model.selectedIndex}` +
      `::${model.passives.join('|')}::${model.radioOn}`;
    if (signature === this.slotSignature) return;
    this.slotSignature = signature;

    this.inventoryEl.innerHTML = '';

    for (let index = 0; index < INVENTORY_SLOTS; index++) {
      const kind = model.hotbar[index];
      const slot = div('slot');
      if (!kind) slot.classList.add('empty');
      // Highlight by slot index, so duplicates of the same item do not all
      // light up together.
      if (kind && index === model.selectedIndex) slot.classList.add('active');

      const key = div('slot-key');
      key.textContent = String(index + 1);

      const label = div('slot-label');
      label.textContent = kind ? (ITEM_LABELS[kind] ?? kind) : '';

      slot.append(key, label);

      if (kind && this.onSlotTap) {
        // Tap-vs-hold lives here rather than in TouchControls because the
        // slots are rebuilt on every inventory change and would orphan
        // externally-attached listeners.
        //
        // Selection happens on the *down* so the highlight answers the thumb
        // immediately, but using waits for the *up* — the same press must not
        // both throw the flare and start the hold-to-drop timer.
        let holdTimer = 0;
        let held = false;
        slot.addEventListener('pointerdown', (e) => {
          e.stopPropagation();
          held = false;
          this.onSlotTap?.(index);
          holdTimer = window.setTimeout(() => {
            held = true;
            this.onSlotHold?.(index);
          }, 550);
        });
        slot.addEventListener('pointerup', () => {
          window.clearTimeout(holdTimer);
          if (!held) this.onSlotUse?.(index);
        });
        const cancelHold = (): void => window.clearTimeout(holdTimer);
        slot.addEventListener('pointercancel', cancelHold);
        // Sliding off a slot is a change of mind: no use, no drop.
        slot.addEventListener('pointerleave', cancelHold);
      }

      this.inventoryEl.appendChild(slot);
    }

    // Passives get a line of text instead of a slot they cannot use.
    if (model.passives.length > 0) {
      this.passivesEl.textContent = `CARRYING  ${model.passives
        .map((kind) => {
          const label = (ITEM_LABELS[kind] ?? kind).toUpperCase();
          // The radio is the one passive with a switch, so its state matters.
          if (kind === 'radio') return `${label} ${model.radioOn ? '[ON]' : '[OFF · R]'}`;
          return label;
        })
        .join('  ·  ')}`;
      this.passivesEl.style.display = 'block';
    } else {
      this.passivesEl.style.display = 'none';
    }
  }

  private renderGear(model: HudModel): void {
    /**
     * These must be shown with an explicit `display: block`.
     *
     * Clearing the inline style instead falls back to the `.gear-panel` rule,
     * which is `display: none` — so the map and compass silently never
     * appeared no matter what the game state said.
     */
    if (model.map) {
      this.mapPanel.canvas.style.display = 'block';
      this.mapPanel.draw(model.map);
    } else {
      this.mapPanel.canvas.style.display = 'none';
    }

    if (model.compass) {
      this.compassStrip.canvas.style.display = 'block';
      this.compassStrip.draw(this.lastYaw);
    } else {
      this.compassStrip.canvas.style.display = 'none';
    }

    if (model.radioSignal !== null) {
      this.radioWrap.style.display = 'flex';
      // Both panels are top-centre; move the radio down when the compass is
      // also on screen so they do not overlap.
      this.radioWrap.classList.toggle('below-compass', model.compass);
      this.radioBar.style.width = `${Math.round(model.radioSignal * 100)}%`;
      this.radioBar.classList.toggle('strong', model.radioSignal > 0.7);
      this.radioLabel.textContent =
        model.radioSignal < 0.04 ? 'NO SIGNAL' : model.radioSignal > 0.7 ? 'STRONG' : 'SIGNAL';
    } else {
      this.radioWrap.style.display = 'none';
    }
  }

  private renderVoice(model: HudModel): void {
    const voice = model.voice;
    if (!voice) {
      this.voiceEl.style.display = 'none';
      this.speakersEl.style.display = 'none';
      return;
    }

    this.voiceEl.style.display = 'block';
    if (voice.status === 'denied') {
      this.voiceEl.textContent = 'MIC BLOCKED';
      this.voiceEl.className = 'voice denied';
    } else if (voice.status === 'starting') {
      this.voiceEl.textContent = 'V  MIC …';
      this.voiceEl.className = 'voice';
    } else {
      this.voiceEl.textContent = voice.muted ? 'V  MIC MUTED' : 'V  MIC LIVE';
      // `loud` warns that the current volume is genuinely dangerous — above a
      // walking pace of noise. Teaching the cost at the moment it is incurred.
      this.voiceEl.className =
        'voice' +
        (voice.muted ? ' muted' : '') +
        (voice.speaking && !voice.muted ? ' speaking' : '') +
        (voice.ownNoise > 0.45 && !voice.muted ? ' loud' : '');
    }

    if (voice.speakers.length > 0) {
      this.speakersEl.style.display = 'block';
      this.speakersEl.textContent = voice.speakers
        .map((name) => `» ${name.toUpperCase()}`)
        .join('\n');
    } else {
      this.speakersEl.style.display = 'none';
    }
  }

  /** Yaw is needed by the compass even when the map is not in hand. */
  private lastYaw = 0;

  setYaw(yaw: number): void {
    this.lastYaw = yaw;
  }

  update(dt: number, model: HudModel): void {
    // Stamina only appears once it is actually a constraint.
    const showStamina = model.stamina < 99.5;
    this.staminaWrap.style.opacity = showStamina ? '1' : '0';
    this.staminaBar.style.width = `${Math.max(0, Math.min(100, model.stamina))}%`;
    this.staminaBar.classList.toggle('low', model.stamina < 25);
    // Exhausted is a distinct state from merely low: it is why sprint is
    // refusing to engage, so it must look different.
    this.staminaBar.classList.toggle('exhausted', model.exhausted);

    const showBreath = model.hiding;
    this.breathWrap.style.opacity = showBreath ? '1' : '0';
    this.breathBar.style.width = `${Math.max(0, Math.min(100, model.breath))}%`;
    this.breathBar.classList.toggle('low', model.breath < 30);

    this.itemEl.textContent = model.heldItem ? (ITEM_HINTS[model.heldItem] ?? '') : '';

    // Torch battery, always visible — it is a resource you must plan around,
    // not a detail hidden behind holding the right item.
    const fraction = clamp01(model.battery / ITEMS.BATTERY_MAX);
    const minutes = Math.floor(model.battery / 60);
    const seconds = Math.floor(model.battery % 60);
    this.batteryBar.style.width = `${fraction * 100}%`;
    this.batteryTime.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    this.batteryEl.classList.toggle('low', model.battery < 45);
    this.batteryEl.classList.toggle('dead', model.battery <= 0);
    this.torchOn = model.flashlightOn;
    this.batteryEl.classList.toggle('lit', this.torchOn);

    this.renderSlots(model);
    this.renderGear(model);
    this.renderVoice(model);

    // Objective line.
    if (model.extractionHold !== null) {
      const remaining = Math.max(0, EXTRACTION.HOLD_TIME - model.extractionHold);
      this.objectiveEl.textContent = `HOLD THE CLEARING  ${remaining.toFixed(0)}s`;
      this.objectiveEl.classList.add('urgent');
    } else if (model.rescueTimer !== null) {
      const m = Math.floor(model.rescueTimer / 60);
      const s = Math.floor(model.rescueTimer % 60);
      this.objectiveEl.textContent = `RESCUE INBOUND  ${m}:${s.toString().padStart(2, '0')}`;
      this.objectiveEl.classList.add('urgent');
    } else {
      this.objectiveEl.textContent = `TOWERS  ${model.towersActivated} / ${model.towersRequired}`;
      this.objectiveEl.classList.remove('urgent');
    }

    // Interaction prompt.
    if (model.prompt) {
      this.promptEl.textContent = model.prompt;
      this.promptEl.classList.add('visible');
    } else {
      this.promptEl.classList.remove('visible');
    }

    // Spectating. "Down", not "dead" — you are unconscious and revivable, and
    // the body on the ground is the thing a teammate has to come and find.
    if (model.spectating) {
      const watching = model.spectatingName
        ? `WATCHING ${model.spectatingName.toUpperCase()}`
        : 'NOBODY LEFT TO WATCH';
      const hint = model.spectateHint ? `  ·  ${model.spectateHint}` : '';
      this.spectateEl.textContent = `YOU ARE DOWN — ${watching}${hint}`;
      this.spectateEl.classList.add('visible');
    } else {
      this.spectateEl.classList.remove('visible');
    }

    // Timers.
    if (this.noticeTimer > 0) {
      this.noticeTimer -= dt;
      if (this.noticeTimer <= 0) this.noticeEl.classList.remove('visible');
    }
    if (this.clueTimer > 0) {
      this.clueTimer -= dt;
      if (this.clueTimer <= 0) this.clueEl.classList.remove('visible');
    }
  }
}

/** Transmit minigame overlay — its own widget because it owns real input. */
export class TransmitPanel {
  private readonly root: HTMLDivElement;
  private readonly needle: HTMLDivElement;
  private readonly target: HTMLDivElement;
  private readonly progressBar: HTMLDivElement;
  private readonly statusEl: HTMLDivElement;

  /** Player-controlled dial position, 0..1. */
  dial = 0.5;
  private visible = false;

  constructor(parent: HTMLElement) {
    this.root = div('transmit');

    const title = div('transmit-title');
    title.textContent = 'DISTRESS BEACON';
    this.root.appendChild(title);

    const dialTrack = div('dial-track');
    this.target = div('dial-target');
    this.needle = div('dial-needle');
    dialTrack.append(this.target, this.needle);
    this.root.appendChild(dialTrack);

    const progressTrack = div('progress-track');
    this.progressBar = div('progress-fill');
    progressTrack.appendChild(this.progressBar);
    this.root.appendChild(progressTrack);

    this.statusEl = div('transmit-status');
    this.root.appendChild(this.statusEl);

    const hint = div('transmit-hint');
    hint.textContent = 'A / D  TUNE     E  ABANDON';
    this.root.appendChild(hint);

    parent.appendChild(this.root);
  }

  show(): void {
    this.visible = true;
    this.root.classList.add('visible');
  }

  hide(): void {
    this.visible = false;
    this.root.classList.remove('visible');
  }

  get isVisible(): boolean {
    return this.visible;
  }

  /** @returns true while the dial sits inside the signal band. */
  update(dt: number, tune: number, signalTarget: number, progress: number): boolean {
    this.dial = clamp01(this.dial + tune * dt * TOWERS.DIAL_SPEED);

    const onSignal = Math.abs(this.dial - signalTarget) <= TOWERS.TOLERANCE;

    // The band can sit partly off the end of the track, so clamp what we draw
    // rather than letting it overflow the container.
    const bandStart = clamp01(signalTarget - TOWERS.TOLERANCE);
    const bandEnd = clamp01(signalTarget + TOWERS.TOLERANCE);

    this.needle.style.left = `${this.dial * 100}%`;
    this.target.style.left = `${bandStart * 100}%`;
    this.target.style.width = `${(bandEnd - bandStart) * 100}%`;
    this.progressBar.style.width = `${progress * 100}%`;

    this.root.classList.toggle('on-signal', onSignal);
    this.statusEl.textContent = onSignal
      ? `TRANSMITTING   ${Math.round(progress * 100)}%`
      : `SIGNAL LOST — RETUNE   ${Math.round(progress * 100)}%`;

    return onSignal;
  }
}

function div(className: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = className;
  return el;
}
